import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import { api } from "../lib/utils";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Award,
  Users,
  FileText,
  Building2,
  Briefcase,
  BarChart3,
  BookOpen,
  Trophy,
  LogOut,
  ChevronRight,
  Clock,
  CheckCircle,
  AlertCircle,
  ClipboardCheck,
  UserCheck,
  Sparkles,
  Bell,
  UserPlus,
  Send,
  Loader2,
} from "lucide-react";
import DashboardLayout from "../components/DashboardLayout";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";

export default function Dashboard() {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [recentCandidates, setRecentCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteData, setInviteData] = useState({ name: "", email: "" });
  const [sendingInvite, setSendingInvite] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [analyticsRes, candidatesRes] = await Promise.all([
        api.get("/analytics/dashboard"),
        api.get("/candidates?limit=5"),
      ]);
      setAnalytics(analyticsRes.data);
      setRecentCandidates(candidatesRes.data.slice(0, 5));
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteData.name.trim() || !inviteData.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setSendingInvite(true);
    try {
      const resp = await api.post("/candidates/quick-invite", inviteData);
      const isReinvite = resp?.data?.re_invited;
      toast.success(
        isReinvite
          ? `Re-invitation sent to ${inviteData.email}. Password reset to User@123.`
          : `Invitation sent to ${inviteData.email}`,
        { duration: 6000 }
      );
      setInviteOpen(false);
      setInviteData({ name: "", email: "" });
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invite");
    } finally {
      setSendingInvite(false);
    }
  };

  const statusColors = {
    draft: "bg-slate-100 text-slate-700",
    documents_uploaded: "bg-blue-100 text-blue-700",
    ai_review: "bg-purple-100 text-purple-700",
    assessor_review: "bg-amber-100 text-amber-700",
    iqa_review: "bg-orange-100 text-orange-700",
    approved: "bg-green-100 text-green-700",
    report_generated: "bg-emerald-100 text-emerald-700",
    closed: "bg-gray-100 text-gray-700",
  };

  const stats = [
    {
      title: "Total Candidates",
      value: analytics?.total_candidates || 0,
      icon: Users,
      color: "bg-blue-500",
    },
    {
      title: "Documents",
      value: analytics?.total_documents || 0,
      icon: FileText,
      color: "bg-purple-500",
    },
    {
      title: "Institutions",
      value: analytics?.total_institutions || 0,
      icon: Building2,
      color: "bg-amber-500",
    },
    {
      title: "IPCB Boards",
      value: analytics?.total_ipcbs || 0,
      icon: Briefcase,
      color: "bg-emerald-500",
    },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="dashboard-container">
        {/* Welcome Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900" data-testid="dashboard-title">
              Welcome back, {user?.name?.split(" ")[0] || "User"}
            </h1>
            <p className="text-slate-500 mt-1">
              Here's an overview of your RPL assessment activity.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="dashboard-invite-btn">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Invite Candidate
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-sm">
                <DialogHeader>
                  <DialogTitle>Invite Candidate</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Full Name *</Label>
                    <Input
                      placeholder="e.g., John Smith"
                      value={inviteData.name}
                      onChange={(e) => setInviteData({ ...inviteData, name: e.target.value })}
                      data-testid="dashboard-invite-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      placeholder="john@example.com"
                      value={inviteData.email}
                      onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                      data-testid="dashboard-invite-email"
                    />
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button>
                    <Button onClick={handleInvite} disabled={sendingInvite} data-testid="dashboard-invite-send">
                      {sendingInvite ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      Send Invite
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            <Link to="/candidates">
              <Button data-testid="new-candidate-btn">
                <Users className="w-4 h-4 mr-2" />
                View Candidates
              </Button>
            </Link>
          </div>
        </div>

        {/* Notification Badges */}
        {!loading && analytics?.notifications?.total_actionable > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="notification-badges">
            {analytics.notifications.pending_approval > 0 && (
              <Link to="/candidates" data-testid="notif-pending-approval">
                <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl hover:border-amber-300 hover:shadow-sm transition-all cursor-pointer">
                  <div className="relative">
                    <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                      <Clock className="w-5 h-5 text-amber-600" />
                    </div>
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                      {analytics.notifications.pending_approval}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-amber-900">Pending Approval</p>
                    <p className="text-xs text-amber-600">Candidates awaiting review</p>
                  </div>
                </div>
              </Link>
            )}
            {analytics.notifications.profile_ready > 0 && (
              <Link to="/candidates" data-testid="notif-profile-ready">
                <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl hover:border-green-300 hover:shadow-sm transition-all cursor-pointer">
                  <div className="relative">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <ClipboardCheck className="w-5 h-5 text-green-600" />
                    </div>
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                      {analytics.notifications.profile_ready}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-green-900">Profile Complete</p>
                    <p className="text-xs text-green-600">Ready for document review</p>
                  </div>
                </div>
              </Link>
            )}
            {analytics.notifications.awaiting_profile > 0 && (
              <Link to="/candidates" data-testid="notif-awaiting-profile">
                <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer">
                  <div className="relative">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <UserCheck className="w-5 h-5 text-blue-600" />
                    </div>
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                      {analytics.notifications.awaiting_profile}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-blue-900">Awaiting Profile</p>
                    <p className="text-xs text-blue-600">Candidates filling profile</p>
                  </div>
                </div>
              </Link>
            )}
            {analytics.notifications.needs_ai_analysis > 0 && (
              <Link to="/candidates" data-testid="notif-needs-ai">
                <div className="flex items-center gap-3 p-4 bg-purple-50 border border-purple-200 rounded-xl hover:border-purple-300 hover:shadow-sm transition-all cursor-pointer">
                  <div className="relative">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-purple-600" />
                    </div>
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-purple-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                      {analytics.notifications.needs_ai_analysis}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-purple-900">Needs AI Analysis</p>
                    <p className="text-xs text-purple-600">Documents uploaded, ready</p>
                  </div>
                </div>
              </Link>
            )}
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <Card key={index} className="border-slate-200" data-testid={`stat-card-${index}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">{stat.title}</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">
                      {loading ? "-" : stat.value}
                    </p>
                  </div>
                  <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Main Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Status Overview */}
          <Card className="lg:col-span-2 border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Cases by Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { status: "draft", label: "Draft", icon: Clock },
                  { status: "ai_review", label: "Credentia Review", icon: FileText },
                  { status: "assessor_review", label: "Assessor Review", icon: Users },
                  { status: "approved", label: "Approved", icon: CheckCircle },
                ].map((item) => (
                  <div
                    key={item.status}
                    className="bg-slate-50 rounded-lg p-4 text-center"
                    data-testid={`status-${item.status}`}
                  >
                    <item.icon className="w-6 h-6 text-slate-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-slate-900">
                      {analytics?.by_status?.[item.status] || 0}
                    </p>
                    <p className="text-xs text-slate-500">{item.label}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* By Level */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">By Qualification Level</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  { level: "doctoral", label: "Doctoral", color: "bg-emerald-500" },
                  { level: "master", label: "Master", color: "bg-amber-500" },
                  { level: "bachelor", label: "Bachelor", color: "bg-purple-500" },
                  { level: "diploma", label: "Diploma", color: "bg-blue-500" },
                ].map((item) => {
                  const count = analytics?.by_level?.[item.level] || 0;
                  const total = analytics?.total_candidates || 1;
                  const percentage = Math.round((count / total) * 100) || 0;
                  return (
                    <div key={item.level} data-testid={`level-${item.level}`}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-600">{item.label}</span>
                        <span className="font-medium text-slate-900">{count}</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${item.color} transition-all duration-500`}
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Candidates */}
        <Card className="border-slate-200">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold">Recent Candidates</CardTitle>
            <Link to="/candidates">
              <Button variant="ghost" size="sm" data-testid="view-all-candidates-btn">
                View All
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse"></div>
                ))}
              </div>
            ) : recentCandidates.length > 0 ? (
              <div className="space-y-3">
                {recentCandidates.map((candidate) => (
                  <Link
                    key={candidate.candidate_id}
                    to={`/candidates/${candidate.candidate_id}`}
                    className="block"
                    data-testid={`candidate-row-${candidate.candidate_id}`}
                  >
                    <div className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-slate-600">
                            {candidate.full_name?.charAt(0) || "?"}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{candidate.full_name}</p>
                          <p className="text-sm text-slate-500">{candidate.email}</p>
                        </div>
                      </div>
                      <Badge className={statusColors[candidate.case_status] || statusColors.draft}>
                        {candidate.case_status?.replace(/_/g, " ")}
                      </Badge>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No candidates yet</p>
                <Link to="/candidates">
                  <Button variant="outline" size="sm" className="mt-3" data-testid="create-first-candidate-btn">
                    Create First Candidate
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-6">
          <Link to="/institutions" className="block" data-testid="quick-action-institutions">
            <Card className="border-slate-200 hover:border-blue-200 hover:shadow-md transition-all cursor-pointer h-full">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Manage Institutions</p>
                  <p className="text-sm text-slate-500">Configure awarding bodies</p>
                </div>
              </CardContent>
            </Card>
          </Link>
          <Link to="/competencies" className="block" data-testid="quick-action-competencies">
            <Card className="border-slate-200 hover:border-purple-200 hover:shadow-md transition-all cursor-pointer h-full">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Competency Frameworks</p>
                  <p className="text-sm text-slate-500">Define assessment criteria</p>
                </div>
              </CardContent>
            </Card>
          </Link>
          <Link to="/analytics" className="block" data-testid="quick-action-analytics">
            <Card className="border-slate-200 hover:border-amber-200 hover:shadow-md transition-all cursor-pointer h-full">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">View Analytics</p>
                  <p className="text-sm text-slate-500">Assessment insights</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>
      </div>
    </DashboardLayout>
  );
}
